import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { AgentEvents, HumanTaskCancelledEvent } from '@dreamai/types';
import { usePusher } from '../../../contexts/PusherContext';
import api from '../../../api';
import HumanTaskDisplay, {
  type HumanTaskDisplayTaskType,
} from './dsl-components/HumanTaskDisplay';
import TaskCancelledModal from './dsl-components/TaskCancelledModal';
import HumanTaskPhoneDialer from './dsl-components/HumanTaskPhoneDialer';
import HumanTaskUserInfo from './dsl-components/HumanTaskUserInfo';
import HumanTaskCalendar from './dsl-components/HumanTaskCalendar';
import IdleNudgeBanner from './IdleNudgeBanner';
import { HumanTaskIdleStatus } from '../humanTaskIdle';
import { AppTab } from './appLayoutConfig';
import './human-task-wrapper.scss';

interface HumanTaskWrapperProps {
  taskId: string;
  threadId?: string;
  tabId?: string;
  updateThreadViewVisualDescription?: (
    visualDescription: string,
  ) => Promise<void>;
  onTabsReady?: (tabs: AppTab[]) => void;
}

interface TaskData {
  id: string;
  taskType: HumanTaskDisplayTaskType;
  title: string;
  description: string;
  status: string;
  taskDetails: any;
  managerThreadId: string | null;
  acceptedAt?: string | null;
  connecteamStatus?: string | null;
  connecteamWorkerName?: string | null;
  connecteamTaskId?: string | null;
  hasCredentials?: boolean;
  hasMandatePayment?: boolean;
  hasVcc?: boolean;
  hasFiles?: boolean;
  mandatePaymentMethod?: string | null;
  cancellationReason?: string | null;
  idleStatus?: HumanTaskIdleStatus | null;
  acceptedByUserId?: string | null;
}

/** Tabs to show for phone call tasks */
const PHONE_CALL_TABS: AppTab[] = [
  { id: 'details', label: 'Instructions' },
  { id: 'phone', label: 'Phone Dialer' },
  { id: 'user-info', label: 'Info' },
  { id: 'calendar', label: 'Calendar' },
];

/** Tabs to show for web action tasks */
const WEB_ACTION_TABS: AppTab[] = [
  { id: 'details', label: 'Instructions' },
  { id: 'user-info', label: 'Info' },
  { id: 'calendar', label: 'Calendar' },
];

/** Tabs to show for in-person tasks */
const IN_PERSON_TABS: AppTab[] = [
  { id: 'details', label: 'Instructions' },
  { id: 'user-info', label: 'Info' },
];

/** Tabs to show for browse session tasks */
const BROWSE_SESSION_TABS: AppTab[] = [
  { id: 'details', label: 'Instructions' },
  { id: 'user-info', label: 'Info' },
];

/** Tabs for terminal phone_call/web_action (no phone dialer, but keep calendar) */
const TERMINAL_WITH_CALENDAR_TABS: AppTab[] = [
  { id: 'details', label: 'Instructions' },
  { id: 'user-info', label: 'Info' },
  { id: 'calendar', label: 'Calendar' },
];

/** Tabs for terminal in_person/browse_session (Instructions + Info only) */
const TERMINAL_TABS: AppTab[] = [
  { id: 'details', label: 'Instructions' },
  { id: 'user-info', label: 'Info' },
];

/**
 * Wrapper component that fetches human task data and renders content.
 * Calls onTabsReady to tell CanvasAssets which tabs to show in the header.
 * - Phone call tasks: Shows task details and phone dialer tabs
 * - Web action tasks: Shows only task details tab
 */
const HumanTaskWrapper: React.FC<HumanTaskWrapperProps> = ({
  taskId,
  threadId,
  tabId = 'details',
  updateThreadViewVisualDescription,
  onTabsReady,
}) => {
  const { getAccessTokenSilently, user } = useAuth0();
  const pusherContext = usePusher(true);
  const opsChannel = pusherContext?.opsChannel;
  const [taskData, setTaskData] = useState<TaskData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const lastTabsRef = useRef<string>('');
  // Cancellation state: modal shown once when the task flips to cancelled
  // while the worker has it open (via Pusher event or polling fallback).
  const [showCancelledModal, setShowCancelledModal] = useState(false);
  const [cancelledWhileMounted, setCancelledWhileMounted] = useState(false);
  const [phoneCallActive, setPhoneCallActive] = useState(false);
  const prevStatusRef = useRef<string | null>(null);

  const fetchTask = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await api.get(`/human-tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success && response.data.task) {
        const task = response.data.task;
        setTaskData({
          id: task.id,
          taskType: task.taskType,
          title: task.title,
          description: task.description,
          status: task.status,
          taskDetails: task.taskDetails,
          managerThreadId: task.managerThreadId || null,
          acceptedAt: task.acceptedAt || null,
          connecteamStatus: task.connecteamStatus || null,
          connecteamWorkerName: task.connecteamWorkerName || null,
          connecteamTaskId: task.connecteamTaskId || null,
          hasCredentials: !!response.data.hasCredentials,
          hasMandatePayment: !!response.data.hasMandatePayment,
          hasVcc: !!response.data.hasVcc,
          hasFiles: !!response.data.hasFiles,
          mandatePaymentMethod: response.data.mandatePaymentMethod || null,
          cancellationReason: task.cancellationReason || null,
          idleStatus: task.idleStatus ?? null,
          acceptedByUserId: task.acceptedByUserId ?? null,
        });

        // Update visual description if callback provided
        if (updateThreadViewVisualDescription) {
          updateThreadViewVisualDescription(`Human Task: ${task.title}`);
        }
      } else {
        setError('Failed to load task');
      }
    } catch (err) {
      console.error('Error fetching human task:', err);
      setError('Failed to load task');
    } finally {
      setLoading(false);
    }
  }, [taskId, getAccessTokenSilently, updateThreadViewVisualDescription]);

  // Reset state when taskId changes
  useEffect(() => {
    setLoading(true);
    setTaskData(null);
    setError(null);
    lastTabsRef.current = ''; // Reset so onTabsReady will be called for new task
    setShowCancelledModal(false);
    setCancelledWhileMounted(false);
    prevStatusRef.current = null;
    fetchTask();
  }, [taskId, fetchTask]);

  const isTerminalStatus = taskData
    ? ['completed', 'cancelled', 'failed'].includes(taskData.status)
    : false;

  // Realtime cancellation: show the blocking modal and refetch the task the
  // moment the requester cancels it.
  useEffect(() => {
    if (!opsChannel) return;

    const handleTaskCancelled = (data: HumanTaskCancelledEvent) => {
      if (data.taskId !== taskId) return;
      setCancelledWhileMounted(true);
      setShowCancelledModal(true);
      fetchTask();
    };

    opsChannel.bind(AgentEvents.HUMAN_TASK_CANCELLED, handleTaskCancelled);
    return () => {
      opsChannel.unbind(AgentEvents.HUMAN_TASK_CANCELLED, handleTaskCancelled);
    };
  }, [opsChannel, taskId, fetchTask]);

  // Polling fallback: while the task is non-terminal, refetch every 30s so a
  // missed Pusher event (dropped connection, background tab) still surfaces
  // the cancellation reasonably quickly.
  useEffect(() => {
    if (!taskData || isTerminalStatus) return;
    const interval = setInterval(fetchTask, 30_000);
    return () => clearInterval(interval);
  }, [taskData, isTerminalStatus, fetchTask]);

  // Detect a status flip to cancelled from any source (event refetch or
  // polling) and surface the modal exactly once.
  useEffect(() => {
    const status = taskData?.status ?? null;
    const prev = prevStatusRef.current;
    prevStatusRef.current = status;
    if (
      status === 'cancelled' &&
      prev !== null &&
      prev !== 'cancelled' &&
      !cancelledWhileMounted
    ) {
      setCancelledWhileMounted(true);
      setShowCancelledModal(true);
    }
  }, [taskData?.status, cancelledWhileMounted]);

  // Notify parent of tabs when task data changes
  useEffect(() => {
    if (!taskData || !onTabsReady) return;

    // For terminal tasks, never show the phone dialer tab — except when the
    // task was cancelled while the worker had it open: keep the dialer
    // mounted so an active call can be wound down politely.
    let tabs: AppTab[];
    if (isTerminalStatus && !cancelledWhileMounted) {
      tabs =
        taskData.taskType === 'phone_call' || taskData.taskType === 'web_action'
          ? TERMINAL_WITH_CALENDAR_TABS
          : TERMINAL_TABS;
    } else {
      tabs =
        taskData.taskType === 'phone_call'
          ? PHONE_CALL_TABS
          : taskData.taskType === 'in_person' ||
              taskData.taskType === 'hotel_payment' ||
              taskData.taskType === 'flight_payment'
            ? IN_PERSON_TABS
            : taskData.taskType === 'browse_session'
              ? BROWSE_SESSION_TABS
              : WEB_ACTION_TABS;
    }
    const tabsKey = JSON.stringify(tabs);

    if (tabsKey !== lastTabsRef.current) {
      lastTabsRef.current = tabsKey;
      onTabsReady(tabs);
    }
  }, [taskData, onTabsReady, isTerminalStatus, cancelledWhileMounted]);

  if (loading) {
    return (
      <div className='human-task-wrapper loading'>
        <p>Loading task...</p>
      </div>
    );
  }

  if (error || !taskData) {
    return (
      <div className='human-task-wrapper error'>
        <p>{error || 'Task not found'}</p>
      </div>
    );
  }

  const taskTypeLabel =
    taskData.taskType === 'phone_call'
      ? 'Phone Call'
      : taskData.taskType === 'in_person'
        ? 'In-Person'
        : taskData.taskType === 'browse_session'
          ? 'Browse Session'
          : taskData.taskType === 'hotel_payment'
            ? 'Hotel Payment'
            : taskData.taskType === 'flight_payment'
              ? 'Flight Payment'
              : 'Web Action';

  const isPhoneCall = taskData.taskType === 'phone_call';
  const isCancelled = taskData.status === 'cancelled';
  const showPhoneDialer =
    isPhoneCall && (!isTerminalStatus || cancelledWhileMounted);

  // Render all tabs simultaneously so components stay mounted (preserving
  // active Twilio calls, etc.) — only the selected tab is visible.
  return (
    <>
      <div style={{ display: tabId === 'details' ? 'contents' : 'none' }}>
        <IdleNudgeBanner
          taskId={taskData.id}
          status={taskData.status}
          idleStatus={taskData.idleStatus}
          isHolder={!!user?.sub && taskData.acceptedByUserId === user.sub}
          onResponded={fetchTask}
        />
        <HumanTaskDisplay
          taskId={taskData.id}
          taskType={taskData.taskType}
          taskTypeLabel={taskTypeLabel}
          title={taskData.title}
          description={taskData.description}
          taskDetails={taskData.taskDetails}
          status={taskData.status}
          connecteamStatus={taskData.connecteamStatus}
          connecteamWorkerName={taskData.connecteamWorkerName}
          readOnly={isTerminalStatus}
          acceptedAt={taskData.acceptedAt}
          connecteamTaskId={taskData.connecteamTaskId}
          hasCredentials={taskData.hasCredentials}
          hasMandatePayment={taskData.hasMandatePayment}
          hasVcc={taskData.hasVcc}
          hasFiles={taskData.hasFiles}
          mandatePaymentMethod={taskData.mandatePaymentMethod}
          threadId={threadId}
          onTaskClosed={fetchTask}
          cancellationReason={taskData.cancellationReason}
        />
      </div>

      {showPhoneDialer && (
        <div style={{ display: tabId === 'phone' ? 'contents' : 'none' }}>
          <HumanTaskPhoneDialer
            taskId={taskData.id}
            phoneNumber={taskData.taskDetails?.phoneNumber || ''}
            contactName={taskData.taskDetails?.contactName}
            cancelled={isCancelled}
            onCallActiveChange={setPhoneCallActive}
          />
        </div>
      )}

      <div style={{ display: tabId === 'user-info' ? 'contents' : 'none' }}>
        <HumanTaskUserInfo taskId={taskData.id} />
      </div>

      {(taskData.taskType === 'phone_call' ||
        taskData.taskType === 'web_action') && (
        <div style={{ display: tabId === 'calendar' ? 'contents' : 'none' }}>
          <HumanTaskCalendar taskId={taskData.id} />
        </div>
      )}

      <TaskCancelledModal
        show={showCancelledModal}
        liveCall={phoneCallActive}
        reason={taskData.cancellationReason}
        onAcknowledge={() => setShowCancelledModal(false)}
      />
    </>
  );
};

export default HumanTaskWrapper;
